import React, { useCallback, useEffect, useState } from 'react';
import { yupResolver } from '@hookform/resolvers/yup';
import {
  Avatar, Button, Dialog, DialogContent, TextField, Typography, Box, Stack,
  DialogActions, DialogTitle, IconButton, CircularProgress, Alert
} from '@mui/material';
import { Controller, useForm } from 'react-hook-form';
import * as yup from 'yup';
import { PhotoCamera as PhotoCameraIcon, Business as BusinessIcon } from '@mui/icons-material';

// Asumo que estos hooks y tipos están correctamente configurados
import { useCompanyCore } from '../../../core/modules/company';
import { useGlobalCore } from '../../../core/modules/global';
import { CREATE_COMPANY_DIALOG } from '../../../core/constants/dialog';
import { PostCompanyResponse } from '../../../core/modules/company/interfaces/post-company';

interface FormData {
  name: string;
  rut: string;
}

const schema = yup.object().shape({
  name: yup.string().required('Debes ingresar un nombre'),
  rut: yup.string().required('Debes ingresar un RUT'),
});

const defaultValues: FormData = {
  name: '',
  rut: '',
};

function CompanyDialog() {
  const { dialog, closeDialog, showSnackbar } = useGlobalCore();
  const { postCompany } = useCompanyCore();

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [apiError, setApiError] = useState<string | null>(null);
  const [selectedAvatar, setSelectedAvatar] = useState<File | null>(null);
  const [previewAvatarUrl, setPreviewAvatarUrl] = useState<string | null>(null);

  const { control, formState, handleSubmit, reset } = useForm({
    mode: 'onChange',
    defaultValues,
    resolver: yupResolver(schema),
  });
  const { isValid, errors, isDirty } = formState;

  // Limpiar el formulario y los errores cuando el diálogo se cierra
  const handleClose = () => {
    closeDialog();
    reset(defaultValues);
    setApiError(null);
    setSelectedAvatar(null);
    if (previewAvatarUrl) {
      URL.revokeObjectURL(previewAvatarUrl);
      setPreviewAvatarUrl(null);
    }
  };

  const onSubmit = useCallback(
    async ({ name, rut }: FormData) => {
      setIsSubmitting(true);
      setApiError(null);

      const onSuccess = (response: PostCompanyResponse) => {
        showSnackbar({ message: response.message, options: { variant: 'success' } });
        handleClose();
      };

      const onFailure = (error: any) => {
        // Manejar errores específicos, como el de duplicado
        if (error.response?.status === 409) {
          setApiError(error.response.data.detail || 'La empresa ya existe.');
        } else {
          setApiError('Ocurrió un error inesperado. Inténtalo de nuevo.');
        }
      };

      // Llamar al hook con los datos y los callbacks
      await postCompany(
        { name, rut, file: selectedAvatar },
        onSuccess,
        onFailure
      );
      
      setIsSubmitting(false);
    },
    [postCompany, selectedAvatar, reset, showSnackbar]
  );

  useEffect(() => {
    // Limpieza del object URL cuando el componente se desmonta
    return () => {
      if (previewAvatarUrl) {
        URL.revokeObjectURL(previewAvatarUrl);
      }
    };
  }, [previewAvatarUrl]);

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files[0]) {
      const file = event.target.files[0];
      setSelectedAvatar(file);
      const newPreviewUrl = URL.createObjectURL(file);
      setPreviewAvatarUrl(newPreviewUrl);
    }
  };

  return (
    <Dialog
      open={dialog === CREATE_COMPANY_DIALOG}
      onClose={handleClose}
      fullWidth
      maxWidth="xs"
    >
      <DialogTitle>
        <Typography variant="h5" component="div" className="font-bold flex items-center gap-2">
            <BusinessIcon />
            Crear Nueva Empresa
        </Typography>
      </DialogTitle>
      
      <form noValidate onSubmit={handleSubmit(onSubmit)}>
        <DialogContent>
            <Stack spacing={3} className="pt-2">
                {apiError && <Alert severity="error">{apiError}</Alert>}

                <Box className="flex flex-col justify-center items-center">
                    <Avatar
                      alt="company avatar"
                      src={previewAvatarUrl || ''}
                      sx={{ width: 100, height: 100, fontSize: '2.5rem', mb: 1 }}
                    >
                        {/* Muestra la inicial del nombre si no hay imagen */}
                        {control._getWatch('name')?.[0]?.toUpperCase()}
                    </Avatar>
                    <Button
                        variant="outlined"
                        component="label"
                        size="small"
                        startIcon={<PhotoCameraIcon />}
                        disabled={isSubmitting}
                    >
                        Subir Logo
                        <input type="file" accept="image/*" hidden onChange={handleFileChange} />
                    </Button>
                </Box>
                
                <Controller
                    control={control}
                    name="name"
                    render={({ field }) => (
                        <TextField
                        {...field}
                        label="Nombre de la Empresa"
                        type="text"
                        error={!!errors.name}
                        helperText={errors.name?.message}
                        variant="outlined"
                        required
                        fullWidth
                        autoFocus
                        />
                    )}
                />

                <Controller
                    control={control}
                    name="rut"
                    render={({ field }) => (
                        <TextField
                        {...field}
                        label="RUT o Identificador"
                        type="text"
                        error={!!errors.rut}
                        helperText={errors.rut?.message}
                        variant="outlined"
                        required
                        fullWidth
                        />
                    )}
                />
            </Stack>
        </DialogContent>
        <DialogActions className="p-4">
          <Button onClick={handleClose} disabled={isSubmitting}>Cancelar</Button>
          <Button
            variant="contained"
            color="primary"
            type="submit"
            disabled={!isValid || !isDirty || isSubmitting}
            startIcon={isSubmitting ? <CircularProgress size={20} color="inherit" /> : null}
          >
            {isSubmitting ? 'Iniciando...' : 'Crear Empresa'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}

export default CompanyDialog;